import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  API_TYPE_GZ,
  API_TYPE_NIRVANA,
  WEB_COMPONENT_CLOUD_STAGING_LIST,
  WEB_COMPONENT_GLOBAL_EXCLUSIONS_APP,
  WEB_COMPONENT_GZ_SIDE_MENU,
  WEB_COMPONENT_MAINTENANCE_WINDOW_GRID,
  WEB_COMPONENT_MAINTENANCE_WINDOWS_APP,
  WEB_COMPONENT_WEB_ACCESS_CONTROL_SCHEDULER_APP, WEB_COMPONENT_WEB_ACCESS_CONTROL_CUSTOM_PAGES_APP,
} from '../../../webcomponents.constants';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { Policy } from '../../../policiesSelector/types';
import { Feature, Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { GzTabGroupWebComponent } from '../../../ui/gzTabs/gzTabGroup';
import { GzTabWebComponent } from '../../../ui/gzTabs/gzTab';
import { GlobalExclusionsAppWebComponent } from '../../exclusionsApp';
import { MaintenanceWindowsAppWebComponent } from '../../maintenanceWindowsApp';
import { WebAccessControlCustomPagesComponent } from '../../webAccessControlCustomPagesApp';
import { WebAccessControlSchedulerComponent } from '../../webAccessControlSchedulerApp';
import { CloudStagingListWebComponent } from '../../cloudStagingList';
import { CompanySelectorWebComponent } from '../../../companySelector';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { ConfigurationProfilesWebComponentSchema } from '../types';

export class ConfigurationProfilesView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $title: HTMLElement;
    $backButton: HTMLElement;
    $backButtonText: HTMLElement;
    $tabGroup: GzTabGroupWebComponent;
    $companySelectorDropdown: CompanySelectorWebComponent;
    $globalExclusionsTab: GzTabWebComponent;
    $maintenanceWindowsTab: GzTabWebComponent;
    $webAccessControlSchedulerTab: GzTabWebComponent;
    $cloudStagingListTab: GzTabWebComponent;
    $wacPagesTab: GzTabWebComponent;
    $globalExclusionsApp: GlobalExclusionsAppWebComponent;
    $maintenanceWindowsApp: MaintenanceWindowsAppWebComponent;
    $webAccessControlSchedulerApp: WebAccessControlSchedulerComponent;
    $wacPagesApp: WebAccessControlCustomPagesComponent;
    $cloudStagingListApp: CloudStagingListWebComponent;
    $detailsPanel: GzSideMenuWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('.title');
    this.dom.$backButton = this.shadow.querySelector('.back_button');
    this.dom.$backButtonText = this.shadow.querySelector('.back_button_text');
    this.dom.$tabGroup = this.shadow.querySelector('#configuration-profiles-tab-group');
    this.dom.$companySelectorDropdown = this.shadow.querySelector('#companySelectorDropdown');
    this.dom.$globalExclusionsTab = this.shadow.querySelector('#global-exclusions-tab');
    this.dom.$maintenanceWindowsTab = this.shadow.querySelector('#maintenance-windows-tab');
    this.dom.$webAccessControlSchedulerTab = this.shadow.querySelector('#web-access-control-scheduler-tab');
    this.dom.$cloudStagingListTab = this.shadow.querySelector('#gz-cloud-staging-list-tab');
    this.dom.$wacPagesTab = this.shadow.querySelector('#wac-pages-tab');
    this.dom.$globalExclusionsApp = this.shadow.querySelector(WEB_COMPONENT_GLOBAL_EXCLUSIONS_APP);
    this.dom.$maintenanceWindowsApp = this.shadow.querySelector(WEB_COMPONENT_MAINTENANCE_WINDOWS_APP);
    this.dom.$webAccessControlSchedulerApp = this.shadow.querySelector(WEB_COMPONENT_WEB_ACCESS_CONTROL_SCHEDULER_APP);
    this.dom.$wacPagesApp = this.shadow.querySelector(WEB_COMPONENT_WEB_ACCESS_CONTROL_CUSTOM_PAGES_APP);
    this.dom.$cloudStagingListApp = this.shadow.querySelector(WEB_COMPONENT_CLOUD_STAGING_LIST);
    this.dom.$detailsPanel = this.shadow.querySelector(WEB_COMPONENT_GZ_SIDE_MENU);

    this.setTranslatedStrings();

    if (!this.isMaintenanceWindowsTabVisible()) {
      this.dom.$maintenanceWindowsTab.hidden = true;
      if (this.dom.$tabGroup && typeof this.dom.$tabGroup.requestUpdate === 'function') {
        this.dom.$tabGroup.requestUpdate();
      }
    }

    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE])) {
      this.dom.$cloudStagingListTab.hidden = true;
      this.dom.$tabGroup.requestUpdate();
    }
  }

  setTranslatedStrings() {
    this.dom.$globalExclusionsTab.label = translateFn('Exclusions');
    this.dom.$maintenanceWindowsTab.label = translateFn('Maintenance Windows');
    this.dom.$webAccessControlSchedulerTab.label = translateFn('WAC Schedulers');
    this.dom.$wacPagesTab.label = translateFn('WAC Custom Pages');
    this.dom.$cloudStagingListTab.label = translateFn('Update Staging');
    this.dom.$title.innerHTML = translateFn('Configuration profiles');
    this.dom.$backButtonText.innerHTML = translateFn('Back');
  }

  async buildView(data: { response: ConfigurationProfilesWebComponentSchema }) {
    const { availableCompanies, selectedTarget } = data.response;
    await this.buildCompanySelectorDropdown(selectedTarget, availableCompanies);
    await this.buildGlobalExclusions();
    await this.buildMaintenanceWindows();
    await this.buildWebAccessControlSchedulers();
    await this.buildCloudStagingList();
    await this.buildWebAccessControlCustomPages();
  }

  async buildWebAccessControlCustomPages() {
    this.dom.$wacPagesApp.injectDependencies({
      ...this.dependencies,
      ...{
        api: {
          type: API_TYPE_NIRVANA,
          clients: this.dependencies.api.clients,
        },
      },
    });
    this.dom.$wacPagesApp.start().catch(console.error);
  }

  async buildCloudStagingList() {
    this.dom.$cloudStagingListApp.injectDependencies({
      ...this.dependencies,
      ...{
        api: {
          type: API_TYPE_NIRVANA,
          clients: this.dependencies.api.clients,
        },
      },
    });
    this.dom.$cloudStagingListApp.start().catch(console.error);
  }

  async buildWebAccessControlSchedulers() {
    this.dom.$webAccessControlSchedulerApp.injectDependencies({
      ...this.dependencies,
      ...{
        api: {
          type: API_TYPE_NIRVANA,
          clients: this.dependencies.api.clients,
        },
      },
    });
    this.dom.$webAccessControlSchedulerApp.start().catch(console.error);
  }

  async buildMaintenanceWindows() {
    if (this.isMaintenanceWindowsTabVisible()) {
      // TODO: remove overwritting of api type in dependencies when dependencies will be injected with gz type from CommonConsole
      this.dom.$maintenanceWindowsApp.injectDependencies({
        ...this.dependencies,
        ...{ api: { type: API_TYPE_GZ, clients: this.dependencies.api.clients } },
      });
      this.dom.$maintenanceWindowsApp.start().catch(console.error);
    }
  }

  async buildGlobalExclusions() {
    if (!this.dom.$globalExclusionsApp.injectDependencies) {
      return;
    }
    this.dom.$globalExclusionsApp.injectDependencies(this.dependencies);
    this.dom.$globalExclusionsApp.start().catch(console.error);
  }

  async buildCompanySelectorDropdown(selectedTarget: Target, availableCompanies: Array<Target>) {
    if (!this.dependencies.canHaveChildCompanies() && !this.dom.$companySelectorDropdown.classList.contains('hidden')) {
      this.dom.$companySelectorDropdown.classList.add('hidden');
      return;
    }
    this.dom.$companySelectorDropdown.classList.remove('hidden');
    this.dom.$companySelectorDropdown.selectedTarget = selectedTarget;
    this.dom.$companySelectorDropdown.availableCompanies = availableCompanies;
    this.dom.$companySelectorDropdown.injectDependencies(this.dependencies);
    this.dom.$companySelectorDropdown.start().catch(console.error);
  }

  showExclusionListDeleteConfirmationDialog(listId: string, policies: Array<Policy>, confirmCallback, listName: string) {
    // compute delete impact
    const impact = document.createElement('div');

    if (policies.length) {
      const table = document.createElement('gz-table');
      table.style.cssText = 'width: 100%';
      policies.forEach(policy => {
        const item = document.createElement('tr');
        item.innerHTML = `<td>${htmlEncode(policy['name'])}</td><td>${policy['policyEndpointsCount']} endpoints</td>`;
        table.appendChild(item);
      });
      impact.appendChild(table);
    }

    let windowText = translateFn('Deleting this list will remove it from all associated policies.');
    if (policies.length === 0) {
      windowText += " ";
      windowText += translateFn("Currently, there is no association between the exclusions in this list and any active policy.");
    }
    const windowBodyHtml = `
      <div>
        <gz-text fontWeight="400" fontSize="14" lineHeight="1.2">
            ${windowText}
        </gz-text>
         <br>${impact.outerHTML}
      </div>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('CANCEL')}
      </gz-button>`;

    const windowTitle = translateFn('Delete ' + listName + '?');

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, confirmCallback, {
      listId: listId,
    });
  }

  /**
   * Returns true if the Maintenance Windows tab is visible
   *
   * @returns bool
   */
  private isMaintenanceWindowsTabVisible(): boolean {
    return (
      this.dependencies.getValidMaintenanceWindowTypes().length > 0 &&
      Application.Main.hasAllRights([Rights.MANAGE_POLICIES_READ])
    );
  }
}
